import React from 'react';
import { Attachment } from '@teetsh/app/src/types/attachment';
import { Spinner } from '@teetsh/app/src/components/Spinner';
import { AcceptOnly, useDropzone } from './useDropzone';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { DropzoneButton } from './DropzoneButton';
import { TFunction } from 'i18next';

function getLabels(
  t: TFunction<['common']>,
  {
    state,
    isDragReject,
    acceptOnly,
    uploadedFileName,
  }: {
    state: 'default' | 'loading' | 'error' | 'success';
    isDragReject: boolean;
    acceptOnly?: AcceptOnly;
    uploadedFileName?: string | null;
  },
): { label: string | null; description: string | null } {
  const getLabel = () => {
    if (isDragReject) {
      return t('common:components.dropzone.dragRejectLabel');
    }
    switch (state) {
      case 'error':
        return t('common:components.dropzone.labels.fileCouldNotBeAdded');
      case 'success':
        return t('common:components.dropzone.labels.fileAddedSuccessfully');
      case 'default':
        return t('common:components.dropzone.labels.selectOrDrop');
      default:
        return null;
    }
  };

  const getDescription = () => {
    if (isDragReject) {
      switch (acceptOnly) {
        case 'csv':
          return t('common:components.dropzone.dragRejectCsv');
        case 'images':
          return t('common:components.dropzone.dragRejectImages');
        default:
          return t('common:components.dropzone.dragRejectGeneric');
      }
    }

    switch (state) {
      case 'error':
        switch (acceptOnly) {
          case 'csv':
            return t('common:components.dropzone.descriptions.errorCsv');
          case 'images':
            return t('common:components.dropzone.descriptions.errorImages');
          default:
            return t('common:components.dropzone.descriptions.errorGeneric');
        }
      case 'success':
        return uploadedFileName
          ? t('common:components.dropzone.descriptions.fileReadyToImport', {
              fileName: uploadedFileName,
            })
          : t(
              'common:components.dropzone.descriptions.fileReadyToImportFallback',
            );
      case 'default':
        if (acceptOnly) {
          switch (acceptOnly) {
            case 'csv':
              return t('common:components.dropzone.descriptions.csvOnly');
            case 'images':
              return t('common:components.dropzone.descriptions.imagesOnly');
          }
        }
        return t('common:components.dropzone.descriptions.genericFileSize');
      default:
        return null;
    }
  };

  return {
    label: getLabel(),
    description: getDescription(),
  };
}

interface Props {
  onReset?: () => void;
  uploadedFileName?: string | null;
  attachments?: Attachment[];
  handleError: ({ message }: { message: string }) => void;
  handleUploadAttachment: (payload: { file: File }) => Promise<any>;
  withoutTitle?: boolean;
  className?: string;
  acceptOnly?: AcceptOnly;
  state?: 'default' | 'loading' | 'error' | 'success';
}

export function CardAttachment(props: Props) {
  const { t } = useTranslation(['common']);
  const {
    attachments = [],
    uploadedFileName,
    onReset,
    handleUploadAttachment,
    handleError,
    acceptOnly,
    state = 'default',
  } = props;

  const isDropzoneDisabled =
    state === 'success' || state === 'error' || state === 'loading';

  const { getRootProps, open, getInputProps, isDragReject, isDragAccept } =
    useDropzone({
      handleUploadAttachment,
      handleError,
      attachments,
      onChange: () => {}, // Not used since we manage state in parent
      acceptOnly,
      disabled: isDropzoneDisabled,
    });

  const { label, description } = getLabels(t, {
    state,
    isDragReject,
    acceptOnly,
    uploadedFileName,
  });

  return (
    <div
      {...getRootProps()}
      data-testid="attachment-area"
      className={props.className}
    >
      <div
        data-testid="attachment-upload"
        tw="border-2 border-dashed border-brand-intense hover:bg-brand-moderate bg-brand-quiet rounded-lg outline-none block text-center p-4"
        css={[
          isDragAccept && tw`border-brand-intense bg-brand-moderate`,
          (state === 'error' || isDragReject) &&
            tw`border-danger-quiet bg-danger-quiet hover:bg-danger-quiet`,
          state === 'success' &&
            tw`border-positive-intense bg-positive-quiet hover:bg-positive-quiet`,
          !isDropzoneDisabled && tw`cursor-pointer`,
        ]}
      >
        <input {...getInputProps()} />
        {state === 'loading' ? (
          <div>
            <Spinner tw="text-primary mx-auto" size="2rem" />
          </div>
        ) : (
          <div>
            {state === 'error' && (
              <XCircleIcon tw="w-6 h-6 mx-auto mb-2 text-danger-moderate" />
            )}
            {state === 'success' && (
              <CheckCircleIcon tw="w-6 h-6 mx-auto mb-2 text-positive-moderate" />
            )}
            {label && (
              <h4
                tw="text-brand-intense font-semibold"
                css={[
                  state === 'success' && tw`text-positive-intense`,
                  (state === 'error' || isDragReject) &&
                    tw`text-danger-intense`,
                ]}
              >
                {label}
              </h4>
            )}
            {description && (
              <div
                tw="text-sm text-brand-moderate"
                css={[
                  (state === 'error' || isDragReject) &&
                    tw`text-danger-moderate`,
                  state === 'success' && tw`text-positive-moderate`,
                ]}
              >
                {description}
              </div>
            )}
            <DropzoneButton
              state={state}
              isDragReject={isDragReject}
              onReset={onReset}
              onOpen={open}
            />
          </div>
        )}
      </div>
    </div>
  );
}
